<template>
  <span
    :class="{
      'is-left': position === 'left',
      'is-top': position === 'top',
      'is-right': position === 'right',
      'is-bottom': position === 'bottom',
      'is-top-left': position === 'top-left',
      'is-top-right': position === 'top-right',
      'is-bottom': position === 'bottom',
      'is-bottom-left': position === 'bottom-left',
      'is-bottom-right': position === 'bottom-right',
      'has-much-text': hasMuchText
    }"
    class="pv-tooltip"
    @mouseenter="onMouseEnter"
  >
    <!-- Content -->
    <span class="pv-tooltip_container">
      <!--
        @slot slot to render the main content; this element will be shown always.
      -->
      <slot />
    </span>
    <!-- Tooltip -->
    <span ref="messageContainer" class="pv-tooltip_message_container">
      <span ref="message" class="pv-tooltip_message">
        <!--
        @slot alternative way to show the message, used when it is needed to include HTML inside tooltip.
        -->
        <slot name="message">{{ message }}</slot>
      </span>
      <div class="pv-tooltip_position-indicator" />
    </span>
  </span>
</template>

<script>
/**
 * Tooltip component with almendra design alignments; the tooltip can be shows in eight different positions;
 * It identifies when there is to many letters (more than 300 characters) in the text and wrap them.
 * @displayName al-tooltip
 */
export default {
  name: 'AlTooltip',
  props: {
    /**
     * Text that will be shown when mouse is over on the element.
     */
    message: {
      type: String,
      default: ''
    },
    /**
     * Prop to defined where it will be render the tooltip element.
     * @values top, bottom, left, right, top-left, top-right, bottom-lef, bottom-right
     */
    position: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      containerWidth: null // tooltip width
    };
  },
  computed: {
    hasMuchText() {
      // check if container width exceeds the limits
      if (this.containerWidth >= 300) {
        return true;
      }
      return false;
    }
  },
  methods: {
    // -------------------------------------------------
    // + Events
    // -------------------------------------------------
    onMouseEnter() {
      this.setWidth();
    },
    // -------------------------------------------------
    // + Methods
    // -------------------------------------------------
    setWidth() {
      this.containerWidth = this.$refs.messageContainer.clientWidth;
    }
  }
};
</script>

<style lang="scss">
// -------------------------------------------------
// + Variables
// -------------------------------------------------
$tooltip_background: color(black);
$tooltip_position-indicator_width: spacing(3);
$tooltip_position-indicator_sides-margin: 2px;
$br-default: 4px;
// -------------------------------------------------
// + Main
// -------------------------------------------------
.pv-tooltip {
  display: inline-block;
  position: relative;
  text-align: center;
  // + States
  &:hover {
    .pv-tooltip_message_container {
      opacity: 1;
      z-index: 1;
    }
  }
  // + Modifiers
  &.has-much-text {
    .pv-tooltip_message_container {
      white-space: normal;
      width: 300px;
    }
  }
  // ++ Position Modifiers
  // +++ Horizontal
  &.is-left,
  &.is-right {
    .pv-tooltip_message_container {
      bottom: 50%;
      transform: translateY(50%);
    }

    .pv-tooltip_position-indicator {
      bottom: auto;
      top: 50%;
      transform: translateY(-50%);
    }
  }

  &.is-left {
    .pv-tooltip_message_container {
      left: auto;
      right: calc(100% + #{$tooltip_position-indicator_width});
    }

    .pv-tooltip_position-indicator {
      left: auto;
      right: -$tooltip_position-indicator_width;

      &::before {
        left: -8px;
        top: 0;
      }
    }
  }

  &.is-right {
    .pv-tooltip_message_container {
      left: calc(100% + #{$tooltip_position-indicator_width});
    }

    .pv-tooltip_position-indicator {
      left: -$tooltip_position-indicator_width;

      &::before {
        right: -8px;
        top: 0;
      }
    }
  }
  // +++ Top + Horizontal
  &.is-top-right,
  &.is-top-left {
    .pv-tooltip_message_container {
      transform: none;
    }

    .pv-tooltip_position-indicator {
      transform: none;
    }
  }

  &.is-top-left {
    .pv-tooltip_message_container {
      left: auto;
      right: -4px;
    }

    .pv-tooltip_position-indicator {
      left: auto;
      right: $tooltip_position-indicator_sides-margin;

      &::before {
        right: 4px;
      }
    }
  }

  &.is-top-right {
    .pv-tooltip_message_container {
      left: -4px;
    }

    .pv-tooltip_position-indicator {
      left: $tooltip_position-indicator_sides-margin;

      &::before {
        right: -4px;
      }
    }
  }
  // +++ Bottom
  &.is-bottom,
  &.is-bottom-left,
  &.is-bottom-right {
    .pv-tooltip_message_container {
      bottom: auto;
      top: calc(100% + #{$tooltip_position-indicator_width});
    }

    .pv-tooltip_position-indicator {
      bottom: 100%;
      top: auto;

      &::before {
        bottom: -8px;
        top: auto;
      }
    }
  }
  // +++ Bottom + Horizontal
  &.is-bottom-left,
  &.is-bottom-right {
    .pv-tooltip_message_container {
      transform: none;
    }

    .pv-tooltip_position-indicator {
      transform: none;
    }
  }

  &.is-bottom-left {
    .pv-tooltip_message_container {
      left: auto;
      right: 0;
    }

    .pv-tooltip_position-indicator {
      left: auto;
      right: 0;

      &::before {
        right: 4px;
      }
    }
  }

  &.is-bottom-right {
    .pv-tooltip_message_container {
      left: 0;
    }

    .pv-tooltip_position-indicator {
      left: 0;

      &::before {
        left: 4px;
        top: auto;
      }
    }
  }
}
// + Message
.pv-tooltip_message_container {
  background-color: $tooltip_background;
  border-radius: $br-default;
  bottom: calc(100% + #{$tooltip_position-indicator_width});
  color: color(white, lighten, 1);
  display: inline-block;
  font-size: font-size(xs);
  left: 50%;
  opacity: 0;
  padding: 4px 8px;
  pointer-events: none;
  position: absolute;
  transform: translateX(-50%);
  transition: $transition_default;
  white-space: nowrap;
  z-index: -1;
}

.pv-tooltip_message {
  display: inline-block;
}
// + Position Indicator
.pv-tooltip_position-indicator {
  height: $tooltip_position-indicator_width;
  left: 50%;
  position: absolute;
  top: 100%;
  transform: translateX(-50%);
  width: $tooltip_position-indicator_width;
  z-index: -1;
  // + Pseudo
  &::before {
    background-color: $tooltip_background;
    border-radius: 3px;
    content: '';
    display: block;
    height: 100%;
    position: absolute;
    top: -8px;
    transform: rotate(45deg);
    width: 100%;
  }
}
</style>
